import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import type { RouterContext } from '@/idv2/routes/router-context';
import { getProceedQueryKey } from '@/idv2/utils/proceed-query';
import { shouldWaitForComponentsSdk } from '@/idv2/utils/route-helpers';

/**
 * Invalidates the proceed query and redirects to the onboarding token route.
 * This is used in loaders/beforeLoad when we want to skip the current step
 * and let the token route determine the next requirement.
 */
export const redirectToCheckRequirements = async (queryClient: QueryClient, token: string): Promise<never> => {
  await queryClient.invalidateQueries({
    type: 'all',
    queryKey: getProceedQueryKey(),
    refetchType: 'none',
  });
  throw redirect({
    to: '/onboarding/$token',
    params: { token },
  });
};

/**
 * Completes the identify flow and redirects based on scope.
 * - auth scope: calls onIdentifyDone then redirects to /complete
 * - onboarding scope: calls onIdentifyDone then redirects to check requirements
 *   (or to wait-for-components-sdk if components SDK relay is needed)
 *
 * Used in loaders/beforeLoad (non-React context).
 */
export const redirectAfterIdentify = async (
  context: Pick<RouterContext, 'scope' | 'onIdentifyDone' | 'queryClient' | 'componentsSdkRelay'>,
  { validationToken, authToken }: { validationToken: string; authToken: string },
): Promise<never> => {
  context.onIdentifyDone?.(validationToken);
  if (context.scope === 'auth') {
    throw redirect({ to: '/complete', search: { validationToken } });
  }
  if (shouldWaitForComponentsSdk(context.componentsSdkRelay)) {
    throw redirect({ to: '/wait-for-components-sdk/$token', params: { token: authToken } });
  }
  throw await redirectToCheckRequirements(context.queryClient, authToken);
};
